import powerUnits from 'shared/constants/json/powerUnits.json';
import capacityUnits from 'shared/constants/json/capacityUnits.json';
import mileageUnits from 'shared/constants/json/mileageUnits.json';
import currencies from 'shared/constants/json/currency.json';
import COUNTRIES from 'shared/constants/json/countries.json';
import statuses from 'shared/constants/json/statuses.json';
import priceStatuses from 'shared/constants/json/priceStatuses.json';
import {
  SpeedIcon,
  GearboxIcon,
  CalendarIcon,
  EngineIcon,
  ChassisIcon,
  LocationIcon,
  ColorIcon,
  VinIcon,
} from 'shared/components/Icons';
import { getTitleOrEmpty, numberWithSpaces } from './common';
import { cleanArray } from './arrays';

export const getFieldFromRootOrCharacteristics = (key, advert) => {
  if (!advert || !key) {
    return null;
  }

  const characteristics = advert.characteristics || {};
  if (advert[key] && advert[key].title && advert[key].id) {
    return advert[key];
  } else if (characteristics[key] && characteristics[key].title && characteristics[key].id) {
    return characteristics[key];
  }

  return null;
};

export const getNumberOrTitleFromRootOrCharacteristics = (key, advert) => {
  const result = getFieldFromRootOrCharacteristics(key, advert);

  if (result && result.number) {
    return result.number;
  } else if (result && result.title) return result.title;

  return result;
};

const getPower = (advert, t) => {
  const powerUnit = powerUnits.find(item => item.id === advert.power_unit);
  const power = getNumberOrTitleFromRootOrCharacteristics('power', advert);

  return power ? `(${power} ${powerUnit && t(powerUnit.title)})` : '';
};

/**
 * @param advert {object}
 * @param key {string} - one of ['capacity', 'capacity_body', 'capacity_bucket']
 * @param t {function}
 * @returns {string}
 */
export const getCapacity = (advert, t, key = 'capacity') => {
  const capacityUnit = capacityUnits.find(item => item.id === advert.capacity_unit);
  const capacity = getNumberOrTitleFromRootOrCharacteristics(key, advert);

  return capacity && capacityUnit ? `${capacity}${t(capacityUnit.title)}` : '';
};

export const getMileageText = (advert, t) => {
  const { mileage, mileage_unit: mileageUnitId, is_new: isNew } = advert;
  const mileageUnit = mileageUnits.find(item => item.id === mileageUnitId);

  let mileageText = mileage > 0 ? `${mileage} ${t(getTitleOrEmpty(mileageUnit))}` : null;
  if (isNew) {
    mileageText = t('новый');
  }

  return mileageText;
};

export const constructModification = (advert, t) => {
  const gearbox = getFieldFromRootOrCharacteristics('gearbox', advert);
  const power = getPower(advert, t);
  const capacity = getCapacity(advert, t);

  return cleanArray([capacity, getTitleOrEmpty(gearbox).toLowerCase(), power]).join(' ').trim();
};

export const getModification = (advert, t) => {
  if (advert.modification && advert.modification.id) {
    return advert.modification.title || '';
  }

  return constructModification(advert, t);
};

export const carPrice = (price, currency) => {
  const priceString = numberWithSpaces(price);
  const currencyTitle = currency.symbol || currency.title;

  return `${priceString} ${currencyTitle}`;
};

export const carPriceBySlug = (prices, currency) => {
  if (!currency || !prices) {
    return '';
  }

  const price = prices.find(item => item.currency === currency.slug);
  if (!price) {
    return '';
  }

  return carPrice(price.value, currency);
};

export const getPriceBySlug = (prices, slug, getNumber = false) => {
  if (!slug || !prices) {
    return '';
  }

  const currency = currencies.find(c => c.slug === slug);
  const price = prices.find(item => item.currency === currency.slug);
  if (!price) {
    return '';
  }
  if (getNumber) return price.value;
  return carPrice(price.value, currency);
};

export const getCurrencyTitleBySlug = slug => {
  const currentCurrencyObj = currencies.find(item => item.slug === slug);
  const { title } = currentCurrencyObj || {};

  return title;
};

export const getSymbolTitleBySlug = slug => {
  const currentCurrencyObj = currencies.find(item => item.slug === slug);
  const { symbol } = currentCurrencyObj || {};

  return symbol;
};

export const getPriceValueBySlug = (prices, slug) => {
  const currentPriceObj = prices.find(price => price.currency === slug);

  return currentPriceObj ? currentPriceObj.value : null;
};

export const getPriceWithCurrency = (prices, slug) => {
  const value = getPriceValueBySlug(prices, slug);
  const currency = getCurrencyTitleBySlug(slug);

  if (typeof value !== 'number' || !currency) {
    return null;
  }

  return slug === 'usd'
    ? `${currency} ${numberWithSpaces(value)}`
    : `${numberWithSpaces(value)} ${currency}`;
};

export const isLocationCountry = location => {
  if (!location || !location.id) {
    return null;
  }

  return !!COUNTRIES.find(country => country.id === location.id);
};

export const convertMlToKm = car => {
  if (car.mileage_unit === 0) {
    return car.mileage;
  } else if (car.mileage_unit === 1) {
    return car.mileage * 1.61;
  }
};

export const getMileageString = (mileage, mileageUnitId) => {
  if (!mileage || typeof mileageUnitId !== 'number') {
    return null;
  }

  const mileageUnitTitle = getTitleOrEmpty(mileageUnits.find(unit => unit.id === mileageUnitId));

  return `${mileage} ${mileageUnitTitle.replace(/[.]/g, '')}`;
};

export const getCapacityUnitTitleById = unitId =>
  getTitleOrEmpty(capacityUnits.find(unit => unit.id === unitId));

export const getCountryById = id => COUNTRIES.find(item => item.id === id);

const translateTitle = (item, intl) =>
  item.title ? { ...item, title: intl.formatMessage({ id: item.title }) } : item;

export const getAdvertStatuses = (advert, intl) => [
  // Get all the statuses that are checked as true
  ...[...statuses, ...priceStatuses, { title: 'авто не в Украине', id: 'abroad' }]
    .filter(status => advert[status.id])
    .map(item => translateTitle(item, intl)),

  // display the 'cleared' status if customs are cleared
  ...(advert.is_not_cleared
    ? []
    : [{ id: '__is_not_cleared', title: `${intl.formatMessage({ id: 'растаможен' })}` }]),

  // check if we has "driven_from" in countries
  ...(advert.driven_from && advert.driven_from.id && !!getCountryById(advert.driven_from.id)
    ? [
        {
          title: `авто ${getCountryById(advert.driven_from.id).from[intl.locale]}`,
          id: 'driven_from',
        },
      ]
    : []),
];

export const getAdvertOgImage = advert =>
  advert && advert.photos && advert.photos.length > 0 && advert.photos[0] && advert.photos[0].image
    ? advert.photos[0].image
    : undefined;

const getTechnicalsSpecification = (advert, item, t) => {
  switch (item) {
    case 'engine':
      return getFieldFromRootOrCharacteristics('engine', advert);
    case 'mileage':
      return {
        slug: item,
        id: 999,
        title: getMileageText(advert, t),
      };
    case 'gearbox':
      return getFieldFromRootOrCharacteristics('gearbox', advert);
    case 'drive':
      return getFieldFromRootOrCharacteristics('drive', advert);
    case 'year':
      return {
        slug: item,
        id: 888,
        title: advert.year,
      };
    case 'location':
      return {
        slug: item,
        id: 777,
        title: advert.location.title,
      };
    default:
      return {
        slug: '',
        id: 0,
        title: '',
      };
  }
};

export const getTransportInfoFiltered = (type = 'avto') => {
  const types = {
    avto: [
      'location',
      'year',
      'modification',
      'mileage',
      'body',
      'color',
      'engine',
      'gearbox',
      'drive',
      'vin',
    ],
  };
  return types[type] ? types[type] : [];
};

export const getIconsByFieldName = () => ({
  mileage: SpeedIcon,
  gearbox: GearboxIcon,
  year: CalendarIcon,
  engine: EngineIcon,
  drive: ChassisIcon,
  location: LocationIcon,
  color: ColorIcon,
  vin: VinIcon,
});

export const getTechnicalsSpecifications = (advert, items, t) => {
  const icons = getIconsByFieldName();
  return items
    .map(slug => {
      const params = getTechnicalsSpecification(advert, slug, t);
      return {
        icon: icons[slug] || null,
        ...params,
      };
    })
    .filter(item => item && item.title);
};



// WEBPACK FOOTER //
// ./src/shared/helpers/transportInfo.js